import { useOfficeComponentContext } from '@office-start/component-context-service';
import { useEncryptionKey, useIdentity } from '@office-start/identity-service';
import { mark as markPerformance } from '@office-start/performance-service';
import { IDLE_VERYLOW_PRIORITY, scheduleWork, } from '@office-start/scheduler-service';
import { getUserItem, setGlobalItem, setUserItem, } from '@office-start/web-storage-utility';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { msaUserHintKey, preloadUserIdStorageKey } from '../models/Constants';
import { decryptCacheEntries } from '../operations/decryptCacheEntries';
import { getCachedLastUiSyncTimestampStorageKey, getCachedSyncTimestampStorageKey, } from '../operations/EdgeworthDb';
import { encryptFiles } from '../operations/encryptFiles';
import { useEdgeworthDataStore } from '../store/useEdgeworthDataStore';
import { mergeAllFilesIntoMap } from '../utilities/mergeAllFilesIntoMap';
import { mergeFavoritesIntoMap } from '../utilities/mergeFavoritesIntoMap';
import { useEdgeworthCachedAllItems } from './useEdgeworthCachedAllItems';
import { useEdgeworthCachedTopItems } from './useEdgeworthCachedTopItems';
import { useEdgeworthDeltaSyncData } from './useEdgeworthDeltaSyncData';
import { useEdgeworthFavorites } from './useEdgeworthFavorites';
import { useEdgeworthRecent } from './useEdgeworthRecent';
/**
 * TODO:
 * - Can potentially write as Document[] instead of EdgeworthSubstrateDeltaSyncFile[]
 */
export function useEdgeworthData(preloadedCacheEntries, filterFileType) {
    var _a = useEdgeworthDataStore(), allFiles = _a.allFiles, allFilesMap = _a.allFilesMap, changedFilesSubject = _a.changedFilesSubject, updateAllFiles = _a.updateAllFiles;
    var cacheWriteMapRef = useRef(new Map());
    var edgeworthReadyLoggedRef = useRef(false);
    var _b = useEncryptionKey(), encryptionKey = _b.data, isEncryptionKeySuccess = _b.isSuccess, isEncryptionKeyIdle = _b.isIdle, isEncryptionKeyLoading = _b.isLoading;
    var _c = useEdgeworthCachedTopItems(preloadedCacheEntries), cachedTopItems = _c.data, isCachedTopItemsSuccess = _c.isSuccess, isCachedTopItemsError = _c.isError;
    var _d = useEdgeworthCachedAllItems(), cachedAllItems = _d.data, isCachedAllItemsSuccess = _d.isSuccess, isCachedAllItemsError = _d.isError;
    var _e = useEdgeworthRecent(filterFileType), recentData = _e.data, isRecentSuccess = _e.isSuccess, isRecentError = _e.isError;
    var _f = useEdgeworthFavorites(), favoritesData = _f.data, isFavoritesSuccess = _f.isSuccess, isFavoritesError = _f.isError;
    var _g = useEdgeworthDeltaSyncData(), deltaSyncData = _g.data, isDeltaSyncError = _g.isError, isInitialSyncStable = _g.isInitialStable, resync = _g.resync, deltaSyncTimestamp = _g.lastSyncTimestamp;
    var identity = useIdentity().data;
    var context = useOfficeComponentContext().data;
    if (!edgeworthReadyLoggedRef.current) {
        if (cachedTopItems || cachedAllItems) {
            markPerformance("Cached_Edgeworth_Ready");
            edgeworthReadyLoggedRef.current = true;
        }
        else if (recentData || deltaSyncData) {
            markPerformance("Edgeworth_Ready");
            edgeworthReadyLoggedRef.current = true;
        }
    }
    var msaUserHintRef = useRef();
    var isInitialStable = (isCachedTopItemsSuccess || isCachedTopItemsError) &&
        (isCachedAllItemsSuccess || isCachedAllItemsError) &&
        (isRecentSuccess || isRecentError) &&
        (isFavoritesSuccess || isFavoritesError) &&
        isInitialSyncStable;
    var mergeFiles = useCallback(function (newFiles, fromFavoritesApi) {
        if (!context) {
            return [];
        }
        // Filter out all EW local files from HWA and using local bridge to get local files
        var filteredNewFiles = newFiles.filter(function (file) {
            return context.workload !== 'pwa' || file.source !== 'LocalFile';
        });
        var newChangedFiles = fromFavoritesApi
            ? mergeFavoritesIntoMap(allFilesMap, filteredNewFiles)
            : mergeAllFilesIntoMap(allFilesMap, filteredNewFiles);
        if (newChangedFiles.length) {
            changedFilesSubject.next(newChangedFiles);
        }
        updateAllFiles();
        return newChangedFiles;
    }, [allFilesMap, changedFilesSubject, context, updateAllFiles]);
    var decryptEntries = useCallback(function (cacheEntries) {
        if (isEncryptionKeySuccess && cacheEntries && cacheEntries.length) {
            decryptCacheEntries(cacheEntries, encryptionKey === null || encryptionKey === void 0 ? void 0 : encryptionKey.key).then(function (decryptedEntries) { return mergeFiles(decryptedEntries); });
        }
    }, [encryptionKey, isEncryptionKeySuccess, mergeFiles]);
    useEffect(function () {
        decryptEntries(cachedTopItems);
    }, [cachedTopItems, decryptEntries]);
    useEffect(function () {
        decryptEntries(cachedAllItems);
    }, [cachedAllItems, decryptEntries]);
    useEffect(function () {
        var _a, _b;
        if ((_b = (_a = recentData === null || recentData === void 0 ? void 0 : recentData.data) === null || _a === void 0 ? void 0 : _a.files) === null || _b === void 0 ? void 0 : _b.length) {
            mergeFiles(recentData.data.files);
        }
    }, [mergeFiles, recentData]);
    useEffect(function () {
        var _a, _b;
        if ((_b = (_a = favoritesData === null || favoritesData === void 0 ? void 0 : favoritesData.data) === null || _a === void 0 ? void 0 : _a.files) === null || _b === void 0 ? void 0 : _b.length) {
            mergeFiles(favoritesData.data.files, true);
        }
    }, [mergeFiles, favoritesData]);
    useEffect(function () {
        var deltaSyncItems = deltaSyncData === null || deltaSyncData === void 0 ? void 0 : deltaSyncData.items;
        if (deltaSyncItems === null || deltaSyncItems === void 0 ? void 0 : deltaSyncItems.length) {
            var changedFiles = mergeFiles(deltaSyncItems);
            changedFiles.forEach(function (file) {
                return cacheWriteMapRef.current.set(file.id, file);
            });
        }
    }, [deltaSyncData, mergeFiles]);
    var syncStorageKeyRef = useRef('');
    if (!syncStorageKeyRef.current) {
        var storageKey = getCachedSyncTimestampStorageKey();
        if (storageKey) {
            syncStorageKeyRef.current = storageKey;
        }
    }
    var syncUiStorageKeyRef = useRef('');
    if (!syncUiStorageKeyRef.current) {
        var storageKey = getCachedLastUiSyncTimestampStorageKey();
        if (storageKey) {
            syncUiStorageKeyRef.current = storageKey;
        }
    }
    // Not passing dependency array because we need this to run everytime
    useEffect(function () {
        // Encrypt and write updated files to cache
        if (cacheWriteMapRef.current.size > 0 &&
            !isEncryptionKeyIdle &&
            !isEncryptionKeyLoading &&
            (identity === null || identity === void 0 ? void 0 : identity.puid) &&
            deltaSyncTimestamp) {
            var arrayToBeEncrypted_1 = Array.from(cacheWriteMapRef.current.values());
            scheduleWork(function () {
                encryptFiles(arrayToBeEncrypted_1, encryptionKey === null || encryptionKey === void 0 ? void 0 : encryptionKey.key).then(function (isSuccess) {
                    if (!isSuccess) {
                        return;
                    }
                    // If we successfully encrypt and/or write to IDB, save last sync timestamp to local storage
                    if (syncStorageKeyRef.current) {
                        setUserItem(identity.puid, syncStorageKeyRef.current, deltaSyncTimestamp);
                    }
                    if (syncUiStorageKeyRef) {
                        setUserItem(identity.puid, syncUiStorageKeyRef.current, new Date().toISOString());
                    }
                    setGlobalItem(preloadUserIdStorageKey, identity.puid);
                });
            }, IDLE_VERYLOW_PRIORITY);
            // Clear write map
            cacheWriteMapRef.current.clear();
        }
    });
    var isError = allFiles.length === 0 &&
        isInitialStable &&
        isRecentError &&
        isFavoritesError &&
        isDeltaSyncError;
    return useMemo(function () {
        /**
         * Unlike AAD, documents metadata for MSA do not contain PUID/AAD Object ID information that we can use to identify ownership
         * Instead, they have OneDrive DriveID, and the current user's DriveID is returned only in diagnostics_data property of EW API
         * The block below takes the property and cache it in local storage for future renders.
         */
        if ((identity === null || identity === void 0 ? void 0 : identity.isMsa) && !msaUserHintRef.current) {
            var msaUserHint = getUserItem(identity.puid, msaUserHintKey);
            if (msaUserHint) {
                msaUserHintRef.current = msaUserHint;
            }
            else {
                var ewUserHint = recentData === null || recentData === void 0 ? void 0 : recentData.data.diagnostics_data.user_hint;
                msaUserHintRef.current = ewUserHint;
                if (ewUserHint) {
                    setUserItem(identity.puid, msaUserHintKey, ewUserHint);
                }
            }
        }
        return {
            data: allFiles,
            isError: isError,
            changedDataSubject: changedFilesSubject,
            isInitialStable: isInitialStable,
            resync: resync,
            msaUserHint: msaUserHintRef.current,
        };
    }, [
        allFiles,
        changedFilesSubject,
        identity,
        isError,
        isInitialStable,
        recentData === null || recentData === void 0 ? void 0 : recentData.data.diagnostics_data.user_hint,
        resync,
    ]);
}
//# sourceMappingURL=useEdgeworthData.js.map